export type ExtractedTableCell = {
  value: string;
  classes: string[];
  colSpan: number;
  rowSpan: number;
  isHeaderCell: boolean;
  isFirstColumn: boolean;
  textColorArgb?: string;
};

export type ExtractedTableRow = {
  cells: ExtractedTableCell[];
  isHeaderRow: boolean;
  rowClasses: string[];
};

export type ExtractedTable = {
  rows: ExtractedTableRow[];
};

export const extractTableFromHtml = (table: HTMLTableElement): ExtractedTable => {
  const toArgb = (cssColor: string): string | undefined => {
    const color = cssColor.trim().toLowerCase();
    if (!color || color === 'transparent' || color === 'inherit') {
      return undefined;
    }

    const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (hexMatch) {
      const hex = hexMatch[1];
      const expanded = hex.length === 3
        ? hex.split('').map(ch => `${ch}${ch}`).join('')
        : hex;
      return `FF${expanded.toUpperCase()}`;
    }

    const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/i);
    if (!rgbMatch) {
      return undefined;
    }

    const channels = rgbMatch[1].split(',').map(part => Number.parseFloat(part.trim()));
    if (channels.length < 3 || channels.slice(0, 3).some(Number.isNaN)) {
      return undefined;
    }

    const alpha = channels.length >= 4 && !Number.isNaN(channels[3])
      ? Math.round(Math.max(0, Math.min(1, channels[3])) * 255)
      : 255;
    const [r, g, b] = channels.slice(0, 3).map(v => Math.max(0, Math.min(255, Math.round(v))));
    const toHex = (v: number) => v.toString(16).padStart(2, '0').toUpperCase();
    return `${toHex(alpha)}${toHex(r)}${toHex(g)}${toHex(b)}`;
  };

  const resolveCellColor = (cell: HTMLTableCellElement): string | undefined => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const candidates: HTMLElement[] = [cell, ...Array.from(cell.querySelectorAll<HTMLElement>('*'))];
    for (const candidate of candidates) {
      const text = (candidate.textContent ?? '').trim();
      if (!text) {
        continue;
      }
      const computedColor = window.getComputedStyle(candidate).color;
      const argb = toArgb(computedColor);
      if (!argb) {
        continue;
      }
      // Skip default black to avoid forcing non-semantic text styling in workbook export.
      if (argb === 'FF000000') {
        continue;
      }
      return argb;
    }
    return undefined;
  };

  const rows: ExtractedTableRow[] = [];
  const tableRows = Array.from(table.rows);

  tableRows.forEach(row => {
    const rowStyle = row.style.display;
    if (rowStyle === 'none') {
      return;
    }
    if (typeof window !== 'undefined') {
      const computed = window.getComputedStyle(row);
      if (computed?.display === 'none') {
        return;
      }
    }
    const isHeaderRow = row.parentElement?.tagName === 'THEAD';
    const rowClasses = Array.from(row.classList);
    const cells = Array.from(row.cells).map((cell, index) => ({
      value: (cell.textContent ?? '').trim(),
      classes: Array.from(cell.classList),
      colSpan: cell.colSpan || 1,
      rowSpan: cell.rowSpan || 1,
      isHeaderCell: cell.tagName === 'TH' || isHeaderRow,
      isFirstColumn: index === 0,
      textColorArgb: resolveCellColor(cell)
    }));
    if (!isHeaderRow && cells.length > 1) {
      const dataCells = cells.slice(1);
      const hasValue = dataCells.some(cell => cell.value !== '' && cell.value !== '-');
      if (!hasValue) {
        return;
      }
    }
    rows.push({ cells, isHeaderRow, rowClasses });
  });

  return { rows };
};
